import { FlashbarProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import {
  GetUserinformationsDocument,
  GetUserinformationsQuery,
  useCreateUserinformationMutation,
  Userinformation,
} from '../../generated/graphql';
import { ErrorText } from '../../lib/ErrorTexts';
import Form from './Form';
import { createDDBItem } from './lib/Helper';

type CreateProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  notifyDeletedOrUpdated: (resource: FlashbarProps.MessageDefinition) => void;
  notifyInProgress: (resource?: FlashbarProps.MessageDefinition) => void;
  notifyFailed: (failedResource: FlashbarProps.MessageDefinition) => void;
};

export type FormValues = {
  name: string;
  range: { type?: string | null; startDate: string; endDate: string };
  description?: string;
  is_closed?: boolean | null;
  notifyUserOnWebsite?: boolean | null;
  notifyStartDate?: { value?: string | null; label?: string | null } | null;
};

export const schema = yup
  .object({
    name: yup.string().required(ErrorText.Required),
    range: yup
      .object({
        type: yup.string().notRequired(),
        startDate: yup.string().required(ErrorText.StartDate),
        endDate: yup.string().required(ErrorText.EndDate),
      })
      .required(ErrorText.Required),
    notifyUserOnWebsite: yup.boolean().notRequired(),
    notifyStartDate: yup
      .object({
        label: yup.string().notRequired(),
        value: yup.string().notRequired(),
      })
      .when('notifyUserOnWebsite', {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      })
      .notRequired(),
  })
  .required();

export default function Create({
  visible,
  setVisible,
  notifyInProgress,
  notifyDeletedOrUpdated,
  notifyFailed,
}: CreateProps) {
  const [createHoliday, { loading: createLoading }] =
    useCreateUserinformationMutation();
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      range: undefined,
      description: undefined,
      notifyUserOnWebsite: false,
      notifyStartDate: undefined,
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: any) => {
    const entry = createDDBItem(data);
    const messageId = uuidv4();
    notifyInProgress({ id: messageId, header: 'Eintrag wird erstellt...' });
    createHoliday({
      variables: { input: entry },
      update: (cache, { data }) => {
        const addData = data?.createUserinformation;
        const existingData = cache.readQuery({
          query: GetUserinformationsDocument,
        }) as GetUserinformationsQuery;
        const oldData = existingData.listUserinformations
          ?.items as Userinformation[];

        cache.writeQuery({
          query: GetUserinformationsDocument,
          data: {
            listUserinformations: { items: [...oldData, addData] },
          },
        });
      },
    })
      .then((data) => {
        const result = data.data?.createUserinformation;
        if (result) {
          notifyInProgress();
          notifyDeletedOrUpdated({
            id: messageId,
            type: 'success',
            content: `Eintrag mit dem Namen "${result.name}" wurde erstellt.`,
            header: 'Gespeichert',
            dismissible: true,
          });
          handleCancel();
        }
      })
      .catch((error) => {
        notifyInProgress();
        notifyFailed({
          id: messageId,
          content: JSON.stringify(error),
          header: 'Ein Fehler ist aufgetreten',
          dismissible: true,
        });
      });
  };

  const handleCancel = useCallback(() => {
    setVisible(false);
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.reset, setVisible]);

  return (
    <FormProvider {...methods}>
      <Modal
        onDismiss={handleCancel}
        visible={visible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={
                  isEmpty(methods.formState.dirtyFields) || createLoading
                }
                formAction="submit"
                onClick={() => methods.handleSubmit(onSubmit)()}
              >
                Speichern
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={'Eintrag erstellen'}
      >
        <Form />
      </Modal>
    </FormProvider>
  );
}
